import type { Branch } from "@domain/CRM/branch";
import type { Company } from "@domain/CRM/company";
import type { Deal } from "@domain/CRM/deal";
import type { Region } from "@domain/CRM/region";

interface JobsDashboardUseCasesDeps {
	getBranchesByCompany(company: Company, fresh?: boolean): Promise<Branch[]>;
	getRegionsByCompany(company: Company, fresh?: boolean): Promise<Region[]>;
	getDealsByCompany(company: Company, fresh?: boolean): Promise<Deal[]>;
}

interface DashboardCompanyContext {
	branches: Branch[];
	regions: Region[];
	deals: Deal[];
}

/**
 * Company-scoped context for the Jobs Dashboard: branches, regions, and deals
 * for the selected company. Thin pass-through to the injected ports — no
 * business logic. Exists so `useCompanyViewsAndJobs` depends on a use case
 * rather than `API.Data.Branches` / `API.Data.Regions` / `API.Data.Deals`
 * directly, mirroring `jobsListUseCases` (the jobs half of the same read
 * path) and `submitJobsUseCases.loadCompanyContext` (Submit Jobs' broader
 * company context, which also pulls growers and contacts).
 */
export interface JobsDashboardUseCases {
	/** The optional `fresh` flag bypasses the server-side read cache for
	 *  Branches/Regions/Deals while still repopulating it — see `useFreshOnEntry`. */
	loadCompanyContext(company: Company, fresh?: boolean): Promise<DashboardCompanyContext>;
}

async function loadCompanyContext(
	deps: JobsDashboardUseCasesDeps,
	company: Company,
	fresh?: boolean,
): Promise<DashboardCompanyContext> {
	const [branches, regions, deals] = await Promise.all([
		deps.getBranchesByCompany(company, fresh),
		deps.getRegionsByCompany(company, fresh),
		deps.getDealsByCompany(company, fresh),
	]);
	return { branches, regions, deals };
}

export function makeJobsDashboardUseCases(deps: JobsDashboardUseCasesDeps): JobsDashboardUseCases {
	return {
		loadCompanyContext: (company, fresh) => loadCompanyContext(deps, company, fresh),
	};
}
